import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { leads as leadsApi, getAccount, masterSupabase } from "../lib/supabase.js";
import { useUserRole, hasPermission } from "../App.jsx";

// ─── Theme ───────────────────────────────────────────────────────────
// Walker-style light tokens, matching Conversations.jsx so the two pages
// feel like part of the same product surface.
const L = {
    bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
    dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
    surface: "#ffffff", surfaceHover: "#f6f5f2", surfaceActive: "#f0efeb", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const inputStyle = { padding: "8px 12px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, fontSize: 12, color: L.text, outline: "none", fontFamily: sans, boxSizing: "border-box" };

// Lead Shepherd temperature chips (hot / warm / cool)
const TEMP_STYLE = {
    hot:  { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    warm: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
    cool: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
};
const selectStyle = { ...inputStyle, padding: "6px 24px 6px 10px", cursor: "pointer" };
const btnGhost = { padding: "7px 12px", background: "none", border: `1px solid ${L.border}`, borderRadius: 8, color: L.muted, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: sans };

// ─── Status configuration ────────────────────────────────────────────
// Statuses match the CHECK constraint on chat_leads.status. Order roughly
// reflects funnel stages: new → contacted → qualified → closed.
const STATUS_CONFIG = {
    new:       { label: "New",       color: "#1d4ed8", border: "#bfdbfe", bg: "#eff6ff" },
    contacted: { label: "Contacted", color: "#6d28d9", border: "#ddd6fe", bg: "#f5f3ff" },
    qualified: { label: "Qualified", color: "#15803d", border: "#bbf7d0", bg: "#f0fdf4" },
    closed:    { label: "Closed",    color: "#6b7280", border: "#e5e7eb", bg: "#f9fafb" },
};
const sc = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.new;

// ─── Utilities ───────────────────────────────────────────────────────
function timeAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString();
}

function exportLeadAsJson(lead) {
    // Right-to-access / right-to-portability support: when a tenant team
    // member needs to fulfill a data subject request, they can download
    // everything we know about this lead as a single JSON file.
    const blob = new Blob([JSON.stringify(lead, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-${lead.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── Brief composer ──────────────────────────────────────────────────
// Like the Conversations Brief — a one-paragraph editorial summary of the
// current state. Keeps the page from feeling like a spreadsheet on first
// load.
function composeBrief({ leads, statusCounts }) {
    if (!leads || leads.length === 0) return null;
    const total = leads.length;
    const fresh = statusCounts.new || 0;
    const recent = leads.filter((l) => (Date.now() - new Date(l.created_at).getTime()) < 86400000 * 7).length;

    const parts = [];
    parts.push(`You have ${total} lead${total !== 1 ? "s" : ""} on file.`);
    if (fresh > 0) parts.push(`${fresh} ${fresh === 1 ? "is" : "are"} new and awaiting follow-up.`);
    if (recent > 0 && recent !== total) parts.push(`${recent} came in this week.`);
    if (statusCounts.qualified > 0) parts.push(`${statusCounts.qualified} ${statusCounts.qualified === 1 ? "has" : "have"} been qualified.`);
    if (statusCounts.contacted > 0) parts.push(`${statusCounts.contacted} ${statusCounts.contacted === 1 ? "is" : "are"} in progress.`);
    return parts.join(" ");
}

// ─── Component ───────────────────────────────────────────────────────
export default function Leads() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const account = getAccount();
    const { role } = useUserRole();
    const canEdit = hasPermission(role, "editContent");

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selected, setSelected] = useState(null);
    const [session, setSession] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [flashMsg, setFlashMsg] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState("");

    // Team members for assignment dropdown
    useEffect(() => {
        if (!account?.id) return;
        masterSupabase
            .from("account_users")
            .select("email, name, role")
            .eq("account_id", account.id)
            .eq("is_active", true)
            .then(({ data }) => setTeamMembers(data || []));
    }, [account?.id]);

    // Load leads
    const loadLeads = useCallback(async () => {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        const { data } = await leadsApi.list(params);
        setLeads(data || []);
        setLoading(false);
    }, [statusFilter]);
    useEffect(() => { loadLeads(); }, [loadLeads]);

    // Honor ?id=... from Conversations "view lead" deep-link
    useEffect(() => {
        const id = searchParams.get("id");
        if (id && leads.length > 0) {
            const found = leads.find((l) => l.id === id);
            if (found) selectLead(found);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, leads]);

    const selectLead = async (lead) => {
        setSelected(lead);
        setSession(null);
        setEditingNotes(false);
        setNotesDraft(lead.notes || "");
        // Lazy-load the originating session so we can link to it
        if (lead.session_id) {
            const { data } = await leadsApi.getSession(lead.session_id);
            setSession(data);
        }
    };

    const refresh = async () => {
        setRefreshing(true);
        await loadLeads();
        if (selected) {
            const { data } = await leadsApi.get(selected.id);
            if (data) setSelected(data);
        }
        setRefreshing(false);
    };

    const flash = (type, text) => { setFlashMsg({ type, text }); setTimeout(() => setFlashMsg(null), 4000); };

    const handleStatus = async (id, status) => {
        const { data, error } = await leadsApi.setStatus(id, status);
        if (error) return flash("error", error.message || "Update failed");
        flash("success", `Marked ${status}`);
        setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
        if (selected?.id === id) setSelected((prev) => ({ ...prev, ...data }));
    };

    const handleAssign = async (id, email) => {
        const { data, error } = await leadsApi.update(id, { assigned_to: email || null });
        if (error) return flash("error", error.message || "Assign failed");
        flash("success", email ? `Assigned to ${email}` : "Unassigned");
        setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
        if (selected?.id === id) setSelected((prev) => ({ ...prev, ...data }));
    };

    const handleNotesSave = async () => {
        if (!selected) return;
        const { data, error } = await leadsApi.update(selected.id, { notes: notesDraft });
        if (error) return flash("error", error.message || "Could not save notes");
        flash("success", "Notes saved");
        setSelected((prev) => ({ ...prev, ...data }));
        setLeads((prev) => prev.map((l) => (l.id === selected.id ? { ...l, ...data } : l)));
        setEditingNotes(false);
    };

    const handleDelete = async (id) => {
        if (!confirm("Permanently delete this lead? This action cannot be undone.\n\nDeleting also removes the link in the conversation transcript.")) return;
        if (selected?.id === id) setSelected(null);
        setLeads((prev) => prev.filter((l) => l.id !== id));
        const { error } = await leadsApi.delete(id);
        if (error) return flash("error", error.message || "Delete failed");
        flash("success", "Lead deleted");
    };
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Permanently delete ${selectedIds.size} lead${selectedIds.size !== 1 ? "s" : ""}? This action cannot be undone.`)) return;
        const ids = [...selectedIds];
        if (selected && selectedIds.has(selected.id)) setSelected(null);
        setLeads((prev) => prev.filter((l) => !selectedIds.has(l.id)));
        setSelectedIds(new Set());
        await leadsApi.deleteMany(ids);
        flash("success", `Deleted ${ids.length} leads`);
    };
    const toggleSelectId = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    // Derived
    const filtered = leads.filter((l) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (l.name || "").toLowerCase().includes(q) ||
            (l.email || "").toLowerCase().includes(q) ||
            (l.phone || "").toLowerCase().includes(q) ||
            (l.message || "").toLowerCase().includes(q) ||
            (l.assigned_to || "").toLowerCase().includes(q);
    });
    const statusCounts = leads.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, { all: leads.length });
    const briefText = composeBrief({ leads, statusCounts });

    const toggleSelectAll = () => {
        if (selectedIds.size === filtered.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(filtered.map((l) => l.id)));
    };

    return (
        <Layout>
            <style>{"@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }"}</style>

            <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, animation: "ov-up 0.5s ease both" }}>
                    <div>
                        <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Leads</h1>
                        <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Contacts captured from your chat widget</p>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {canEdit && selectedIds.size > 0 && <button onClick={handleBulkDelete} style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }}>Delete {selectedIds.size}</button>}
                        <button onClick={refresh} disabled={refreshing} style={{ ...btnGhost, cursor: refreshing ? "not-allowed" : "pointer" }}>{refreshing ? "..." : "↻ Refresh"}</button>
                    </div>
                </div>

                {/* Brief */}
                {briefText && (
                    <div style={{ padding: "20px 0 18px", borderBottom: `1px solid ${L.border}`, marginBottom: 22, animation: "ov-up 0.5s ease 0.04s both" }}>
                        <div style={{ fontSize: 14, lineHeight: 1.75, color: L.textSoft, fontFamily: sans, maxWidth: 680 }}>{briefText}</div>
                    </div>
                )}

                {/* Flash */}
                {flashMsg && (
                    <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 12, background: flashMsg.type === "error" ? "#fef2f2" : "#f0fdf4", color: flashMsg.type === "error" ? "#dc2626" : "#15803d", border: `1px solid ${flashMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`, animation: "ov-up 0.3s ease both" }}>{flashMsg.text}</div>
                )}

                {/* Stats */}
                <div style={{ display: "flex", gap: 0, marginBottom: 20, borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}`, animation: "ov-up 0.5s ease 0.05s both" }}>
                    {[
                        { label: "Total", value: leads.length },
                        { label: "New", value: statusCounts.new || 0, highlight: (statusCounts.new || 0) > 0 },
                        { label: "Contacted", value: statusCounts.contacted || 0 },
                        { label: "Qualified", value: statusCounts.qualified || 0 },
                        { label: "Closed", value: statusCounts.closed || 0 },
                    ].map((s, i, arr) => (
                        <div key={s.label} style={{ padding: "14px 18px", flex: 1, borderRight: i < arr.length - 1 ? `1px solid ${L.border}` : "none" }}>
                            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 4 }}>{s.label}</div>
                            <div style={{ fontSize: 20, fontWeight: 300, letterSpacing: "-0.02em", color: s.highlight ? "#1d4ed8" : L.text }}>{s.value}</div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.08s both" }}>
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone, message..." style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
                    {["", "new", "contacted", "qualified", "closed"].map((s) => {
                        const active = statusFilter === s;
                        const cfg = s ? sc(s) : null;
                        return (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                style={{
                                    padding: "7px 12px", borderRadius: 8, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                    fontFamily: sans, transition: "all 0.15s",
                                    background: active && cfg ? cfg.bg : "none",
                                    color: active && cfg ? cfg.color : L.muted,
                                    border: `1px solid ${active && cfg ? cfg.border : L.border}`,
                                }}
                            >
                                {s ? STATUS_CONFIG[s].label : "All"} {s ? statusCounts[s] || 0 : statusCounts.all}
                            </button>
                        );
                    })}
                </div>

                {/* Split pane */}
                <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20, alignItems: "start", animation: "ov-up 0.5s ease 0.1s both" }}>
                    {/* Lead list */}
                    <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, overflow: "hidden", maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
                        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${L.border}`, fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", display: "flex", justifyContent: "space-between" }}>
                            {canEdit && <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} style={{ cursor: "pointer", marginRight: 8 }} />}
                            <span>Leads</span><span>{filtered.length}</span>
                        </div>
                        {loading ? (
                            <div style={{ padding: "40px 20px", color: L.muted, fontSize: 13, textAlign: "center" }}>Loading...</div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: "48px 20px", color: L.muted, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
                                {leads.length === 0
                                    ? "No leads yet. They'll appear here when visitors submit the contact form in your chat widget."
                                    : `No ${statusFilter || ""} leads match your filters.`}
                            </div>
                        ) : (
                            filtered.map((l) => {
                                const isSelected = selected?.id === l.id;
                                const cfg = sc(l.status);
                                return (
                                    <div
                                        key={l.id}
                                        onClick={() => selectLead(l)}
                                        style={{
                                            padding: "14px 18px",
                                            cursor: "pointer",
                                            borderBottom: `1px solid ${L.borderLight}`,
                                            borderLeft: isSelected ? `3px solid ${cfg.color}` : "3px solid transparent",
                                            background: isSelected ? L.surfaceActive : L.surface,
                                            transition: "background 0.1s",
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = L.surfaceHover; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = L.surface; }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                            {canEdit && <input type="checkbox" checked={selectedIds.has(l.id)} onChange={() => toggleSelectId(l.id)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer", flexShrink: 0 }} />}
                                            <span style={{ fontSize: 13, fontWeight: 500, flex: 1, color: L.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                                            <span style={{ fontSize: 11, color: L.dim, flexShrink: 0 }}>{timeAgo(l.created_at)}</span>
                                        </div>
                                        <div style={{ display: "flex", gap: 6, paddingLeft: canEdit ? 22 : 0, flexWrap: "wrap", alignItems: "center" }}>
                                            <span style={{ fontSize: 11, color: L.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{l.email}</span>
                                            <span style={{
                                                fontSize: 10, padding: "1px 8px", borderRadius: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                                                background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                                            }}>{cfg.label}</span>
                                            {l.shepherd?.temperature && (
                                                <span title={`Lead Shepherd read this lead as ${l.shepherd.temperature}`} style={{
                                                    fontSize: 10, padding: "1px 8px", borderRadius: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                                                    background: TEMP_STYLE[l.shepherd.temperature]?.bg, color: TEMP_STYLE[l.shepherd.temperature]?.color, border: `1px solid ${TEMP_STYLE[l.shepherd.temperature]?.border}`,
                                                }}>{l.shepherd.temperature}</span>
                                            )}
                                            {l.assigned_to && <span style={{ fontSize: 10, color: "#6d28d9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 100 }}>→ {l.assigned_to.split("@")[0]}</span>}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Detail panel */}
                    <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, maxHeight: "calc(100vh - 380px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        {selected ? (
                            <>
                                {/* Header */}
                                <div style={{ padding: "18px 24px", borderBottom: `1px solid ${L.border}`, flexShrink: 0 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 20, fontWeight: 500, color: L.text, marginBottom: 4 }}>{selected.name}</div>
                                            <div style={{ fontSize: 12, color: L.muted }}>{timeAgo(selected.created_at)} · received {new Date(selected.created_at).toLocaleString()}</div>
                                        </div>
                                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                            <button onClick={() => exportLeadAsJson(selected)} style={{ ...btnGhost, fontSize: 11 }} title="Download all data we have on this lead as JSON — supports right-to-access requests.">Export</button>
                                            {canEdit && <button onClick={() => handleDelete(selected.id)} style={{ ...btnGhost, fontSize: 11, color: "#dc2626", borderColor: "#fecaca" }}>Delete</button>}
                                        </div>
                                    </div>
                                    {canEdit && (
                                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                            <select value={selected.status} onChange={(e) => handleStatus(selected.id, e.target.value)} style={{ ...selectStyle, color: sc(selected.status).color, borderColor: sc(selected.status).border }}>
                                                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                            </select>
                                            <select value={selected.assigned_to || ""} onChange={(e) => handleAssign(selected.id, e.target.value)} style={selectStyle}>
                                                <option value="">Unassigned</option>
                                                {teamMembers.map((m) => <option key={m.email} value={m.email}>{m.name || m.email}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Body */}
                                <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
                                    {/* Contact card */}
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 10 }}>Contact</div>
                                        <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                                            <Row label="Email">
                                                <a href={`mailto:${selected.email}`} style={{ color: L.text, textDecoration: "none", borderBottom: `1px solid ${L.border}` }}>{selected.email}</a>
                                            </Row>
                                            {selected.phone && (
                                                <Row label="Phone">
                                                    <a href={`tel:${selected.phone}`} style={{ color: L.text, textDecoration: "none", borderBottom: `1px solid ${L.border}` }}>{selected.phone}</a>
                                                </Row>
                                            )}
                                            {selected.contacted_at && (
                                                <Row label="Contacted at">
                                                    <span style={{ color: L.textSoft }}>{new Date(selected.contacted_at).toLocaleString()}</span>
                                                </Row>
                                            )}
                                        </div>
                                    </div>

                                    {/* Message */}
                                    {selected.message && (
                                        <div style={{ marginBottom: 24 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 10 }}>Their message</div>
                                            <div style={{ padding: "12px 16px", background: L.inputBg, borderRadius: 8, border: `1px solid ${L.borderLight}`, fontSize: 13, lineHeight: 1.65, color: L.text, whiteSpace: "pre-wrap" }}>{selected.message}</div>
                                        </div>
                                    )}

                                    {/* Lead Shepherd brief */}
                                    {selected.shepherd && (
                                        <div style={{ marginBottom: 24 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                                <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted }}>Shepherd brief</span>
                                                {selected.shepherd.temperature && (
                                                    <span style={{
                                                        fontSize: 10, padding: "1px 8px", borderRadius: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                                                        background: TEMP_STYLE[selected.shepherd.temperature]?.bg, color: TEMP_STYLE[selected.shepherd.temperature]?.color, border: `1px solid ${TEMP_STYLE[selected.shepherd.temperature]?.border}`,
                                                    }}>{selected.shepherd.temperature}</span>
                                                )}
                                            </div>
                                            <div style={{ padding: "12px 16px", background: L.inputBg, borderRadius: 8, border: `1px solid ${L.borderLight}`, fontSize: 13, lineHeight: 1.65, color: L.text }}>
                                                {selected.shepherd.summary && <div style={{ marginBottom: selected.shepherd.suggested_action || selected.shepherd.signals?.length ? 10 : 0 }}>{selected.shepherd.summary}</div>}
                                                {selected.shepherd.suggested_action && (
                                                    <div style={{ marginBottom: selected.shepherd.signals?.length ? 10 : 0 }}>
                                                        <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: L.muted, marginRight: 8 }}>Next step</span>
                                                        {selected.shepherd.suggested_action}
                                                    </div>
                                                )}
                                                {Array.isArray(selected.shepherd.signals) && selected.shepherd.signals.length > 0 && (
                                                    <div style={{ display: "grid", gap: 4 }}>
                                                        {selected.shepherd.signals.map((s, i) => (
                                                            <div key={i} style={{ fontSize: 12, color: L.textSoft, fontStyle: "italic" }}>"{s}"</div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 10, color: L.dim, marginTop: 6 }}>
                                                Drafted by the Lead Shepherd{selected.shepherd.from_transcript ? " from the full conversation" : " from the submission message"}{selected.shepherd.generated_at ? ` · ${new Date(selected.shepherd.generated_at).toLocaleDateString()}` : ""} — status, notes, and assignment stay yours.
                                            </div>
                                        </div>
                                    )}

                                    {/* Context */}
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 10 }}>Context</div>
                                        <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                                            {selected.page_url && (
                                                <Row label="Page">
                                                    <a href={selected.page_url} target="_blank" rel="noopener noreferrer" style={{ color: L.text, textDecoration: "none", borderBottom: `1px solid ${L.border}`, wordBreak: "break-all" }}>{selected.page_url}</a>
                                                </Row>
                                            )}
                                            {selected.session_id && (
                                                <Row label="Conversation">
                                                    <a
                                                        href="#"
                                                        onClick={(e) => { e.preventDefault(); navigate("/conversations"); }}
                                                        style={{ color: L.text, textDecoration: "none", borderBottom: `1px solid ${L.border}` }}
                                                        title="Jump to Conversations to see the full transcript."
                                                    >
                                                        View transcript →
                                                    </a>
                                                </Row>
                                            )}
                                            {selected.intent_snapshot?.last_message && (
                                                <Row label="Last question">
                                                    <span style={{ color: L.textSoft, fontStyle: "italic" }}>"{selected.intent_snapshot.last_message}"</span>
                                                </Row>
                                            )}
                                            {selected.user_agent && (
                                                <Row label="Browser">
                                                    <span style={{ color: L.muted, fontSize: 11, wordBreak: "break-all" }}>{selected.user_agent}</span>
                                                </Row>
                                            )}
                                        </div>
                                    </div>

                                    {/* Internal notes */}
                                    <div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted }}>Internal notes</div>
                                            {canEdit && !editingNotes && <button onClick={() => { setEditingNotes(true); setNotesDraft(selected.notes || ""); }} style={{ ...btnGhost, fontSize: 10, padding: "4px 10px" }}>{selected.notes ? "Edit" : "Add note"}</button>}
                                        </div>
                                        {editingNotes ? (
                                            <div>
                                                <textarea
                                                    value={notesDraft}
                                                    onChange={(e) => setNotesDraft(e.target.value)}
                                                    rows={4}
                                                    style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.65, padding: "10px 12px", fontSize: 13 }}
                                                    placeholder="Internal notes — visible only to your team."
                                                    autoFocus
                                                />
                                                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                                                    <button onClick={handleNotesSave} style={{ padding: "6px 14px", background: L.text, color: L.surface, border: "none", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>Save</button>
                                                    <button onClick={() => { setEditingNotes(false); setNotesDraft(selected.notes || ""); }} style={btnGhost}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : selected.notes ? (
                                            <div style={{ padding: "12px 16px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13, lineHeight: 1.65, color: "#92400e", whiteSpace: "pre-wrap" }}>{selected.notes}</div>
                                        ) : (
                                            <div style={{ fontSize: 12, color: L.dim, fontStyle: "italic" }}>No notes yet.</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div style={{ padding: 60, textAlign: "center", color: L.muted, fontSize: 13 }}>
                                Select a lead to see details.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

// ─── Sub-components ──────────────────────────────────────────────────
function Row({ label, children }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, alignItems: "baseline" }}>
            <div style={{ fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{label}</div>
            <div>{children}</div>
        </div>
    );
}
